import { useEffect, useState, useCallback } from "react";

/**
 * Captures the PWA install lifecycle:
 *  - On Chromium-based browsers (Android, desktop Chrome) it intercepts the
 *    `beforeinstallprompt` event so the app can show its own install CTA.
 *  - On iOS Safari (which does NOT fire that event) it detects the platform
 *    and exposes `isIOS` so the UI can render the manual "Add to Home Screen"
 *    instructions.
 *  - It also detects when the app is already running in standalone PWA mode
 *    (no install needed) and when a previous prompt was dismissed.
 */
export default function useInstallPrompt() {
  const [deferredEvt, setDeferredEvt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const isStandalone = typeof window !== "undefined" && (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true ||
    document.referrer.startsWith("android-app://")
  );
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);

  useEffect(() => {
    const onBefore = (e) => { e.preventDefault(); setDeferredEvt(e); };
    const onInstalled = () => { setInstalled(true); setDeferredEvt(null); };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredEvt) return null;
    deferredEvt.prompt();
    const choice = await deferredEvt.userChoice;
    setDeferredEvt(null);
    return choice?.outcome; // "accepted" | "dismissed"
  }, [deferredEvt]);

  return {
    canInstall: !!deferredEvt,
    isStandalone: !!isStandalone,
    isInstalled: installed,
    isIOS,
    isAndroid,
    promptInstall,
  };
}
